// Stage timing (RALLY_GOAL.md §13): start countdown with lights and false-start detection, split
// and flying-finish crossings interpolated between frames, time penalties, and local best times.
// Pure logic on the sim clock (pauses with the game, deterministic in tests).

export const LIGHTS = 5;
export const LIGHT_INTERVAL = 1;
/** Penalties (s). False start: the FIA's first-offence penalty; reset: this game's choice (DECISIONS.md). */
export const FALSE_START_PENALTY = 10;
export const RESET_PENALTY = 5;
/** Creeping forward this far (m) before green is a false start. */
export const FALSE_START_DISTANCE = 0.6;

export type TimingPhase = 'grid' | 'countdown' | 'running' | 'finished';

export interface Penalty {
  reason: 'false-start' | 'reset';
  seconds: number;
  /** Stage time when it was given (s; negative = before the start). */
  at: number;
}

export interface StageResult {
  /** Driving time from green to the flying finish (s). */
  time: number;
  penalties: number;
  total: number;
  /** Cumulative split times (s, without penalties). */
  splits: number[];
}

export type TimingEvent =
  | { kind: 'light'; lit: number }
  | { kind: 'green' }
  | { kind: 'false-start' }
  | { kind: 'split'; index: number; time: number }
  | { kind: 'finish'; result: StageResult };

export interface TimingLayout {
  startS: number;
  splits: number[];
  finishS: number;
}

export class StageTiming {
  phase: TimingPhase = 'grid';
  /** Sim time of the green light. */
  startTime = 0;
  private countdownAt = 0;
  private gridS = 0;
  lit = 0;
  falseStart = false;
  splits: number[] = [];
  finish: StageResult | null = null;
  penalties: Penalty[] = [];
  private prevS = 0;
  private prevT = 0;
  private readonly events: TimingEvent[] = [];

  constructor(readonly layout: TimingLayout) {}

  /** Car at the grid position `gridS`: clear everything. */
  reset(gridS: number): void {
    this.phase = 'grid';
    this.gridS = gridS;
    this.lit = 0;
    this.falseStart = false;
    this.splits = [];
    this.finish = null;
    this.penalties = [];
    this.prevS = gridS;
  }

  /** Start the light sequence at sim time `now`. */
  beginCountdown(now: number): void {
    this.phase = 'countdown';
    this.countdownAt = now;
    this.lit = 0;
  }

  get penaltySeconds(): number {
    let p = 0;
    for (const x of this.penalties) p += x.seconds;
    return p;
  }

  /** Stage time now (s): 0 before green, frozen at the finish. */
  elapsed(now: number): number {
    if (this.phase === 'finished' && this.finish) return this.finish.time;
    if (this.phase !== 'running') return 0;
    return now - this.startTime;
  }

  addPenalty(reason: Penalty['reason'], seconds: number, now: number): void {
    this.penalties.push({ reason, seconds, at: this.phase === 'running' ? now - this.startTime : -1 });
  }

  /** Per frame (or sim step) with the car's distance along the stage. Returns this frame's events. */
  update(now: number, s: number): readonly TimingEvent[] {
    const ev = this.events;
    ev.length = 0;
    if (this.phase === 'countdown') {
      const t = now - this.countdownAt;
      const lit = Math.min(LIGHTS, Math.floor(t / LIGHT_INTERVAL) + 1);
      if (lit !== this.lit && t < LIGHTS * LIGHT_INTERVAL) {
        this.lit = lit;
        ev.push({ kind: 'light', lit });
      }
      if (!this.falseStart && s > this.gridS + FALSE_START_DISTANCE) {
        this.falseStart = true;
        this.addPenalty('false-start', FALSE_START_PENALTY, now);
        ev.push({ kind: 'false-start' });
      }
      if (t >= LIGHTS * LIGHT_INTERVAL) {
        this.phase = 'running';
        this.startTime = this.countdownAt + LIGHTS * LIGHT_INTERVAL;
        this.lit = 0;
        ev.push({ kind: 'green' });
        this.prevS = s;
        this.prevT = now;
      }
      return ev;
    }
    if (this.phase !== 'running') return ev;
    const L = this.layout;
    const cross = (line: number): number | null => {
      if (this.prevS < line && s >= line) {
        const f = s > this.prevS ? (line - this.prevS) / (s - this.prevS) : 1;
        return this.prevT + f * (now - this.prevT) - this.startTime;
      }
      return null;
    };
    while (this.splits.length < L.splits.length) {
      const i = this.splits.length;
      const t = cross(L.splits[i]);
      if (t === null) break;
      this.splits.push(t);
      ev.push({ kind: 'split', index: i, time: t });
    }
    const tf = cross(L.finishS);
    if (tf !== null) {
      // A split missed by a reset past it counts as the finish time ordering allows.
      while (this.splits.length < L.splits.length) this.splits.push(tf);
      const pen = this.penaltySeconds;
      this.finish = { time: tf, penalties: pen, total: tf + pen, splits: [...this.splits] };
      this.phase = 'finished';
      ev.push({ kind: 'finish', result: this.finish });
    }
    // Only forward progress counts (a reset back down the road must not re-cross lines).
    if (s > this.prevS) this.prevS = s;
    this.prevT = now;
    return ev;
  }
}

export interface BestEntry extends StageResult {
  date: string;
  build: string;
}

export interface BestTimesStore {
  get(stageId: string, carId: string): BestEntry | null;
  /** Saves if it beats the current best; returns true when it is a new personal best. */
  submit(stageId: string, carId: string, r: StageResult, build: string): boolean;
}

export function bestTimesStore(key: string, storage: Pick<Storage, 'getItem' | 'setItem'> | null = safeLocalStorage()): BestTimesStore {
  const read = (): Record<string, BestEntry> => {
    try {
      const o = JSON.parse(storage?.getItem(key) ?? '{}');
      return o && typeof o === 'object' ? o : {};
    } catch {
      return {};
    }
  };
  let mem = read();
  return {
    get(stageId, carId) {
      const e = mem[`${stageId}|${carId}`];
      return e && Number.isFinite(e.total) && Array.isArray(e.splits) ? e : null;
    },
    submit(stageId, carId, r, build) {
      const k = `${stageId}|${carId}`;
      const cur = mem[k];
      if (cur && Number.isFinite(cur.total) && cur.total <= r.total) return false;
      mem = { ...read(), [k]: { ...r, date: new Date().toISOString(), build } };
      try {
        storage?.setItem(key, JSON.stringify(mem));
      } catch {
        /* storage unavailable: best time kept for this visit */
      }
      return true;
    },
  };
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

/** m:ss.cc */
export function formatTime(t: number): string {
  if (!Number.isFinite(t)) return '–:––.––';
  const neg = t < 0;
  const cs = Math.round(Math.abs(t) * 100);
  const m = Math.floor(cs / 6000);
  const s = Math.floor((cs % 6000) / 100);
  const c = cs % 100;
  return `${neg ? '-' : ''}${m}:${String(s).padStart(2, '0')}.${String(c).padStart(2, '0')}`;
}

/** +1.23 / -0.45 */
export function formatDelta(d: number): string {
  const sign = d < 0 ? '−' : '+';
  return `${sign}${Math.abs(d).toFixed(2)}`;
}
